import React, { useState } from 'react';
import { useIntl } from "react-intl";
import moment from "moment";
import classNames from "classnames";
import statisticsRepository from "@/repository/statisticsRepository";
import { useStatCounts } from "@/query/statistics";
import { useAgentId } from "@/hook";
import Button from "@/component/Button";
import { DateRangePicker, DateRange } from "@/component/dateRangePicker";
import { DownloadIcon } from "@/assets/svgs/icons";

import AgentSummary from "./component/AgentSummary";
import IntentStatTable from "./component/IntentStatTable";
import SentenceLineChart from "./component/SentenceLineChart";
import UserSentencesTable from "./component/UserSentencesTable";


const DashboardView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();

    const [period, setPeriod] = useState<DateRange>({
        start: moment().startOf('day').subtract(14, 'days'),
        end: moment().endOf('day'),
    });

    const { data: statCounts } = useStatCounts(
        agentId, 
        period.start?.format("YYYY-MM-DD HH:mm:ss"), 
        period.end?.format("YYYY-MM-DD HH:mm:ss"),
    );

    const onClickDownload = () => {
        if (!period.start || !period.end) {
            return;
        }
        statisticsRepository.exportUserSentences(
            agentId,
            period.start.format("YYYY-MM-DD HH:mm:ss"),
            period.end.format("YYYY-MM-DD HH:mm:ss"),
        );
    };

    return (
        <div className="agent-content-layout">
            <div className="w-[1200px] flex flex-col gap-3">
                <AgentSummary />

                <div className="w-full px-6 py-5 flex flex-col gap-7 rounded-[6px] border border-black-200 bg-white">
                    <div className="flex items-center gap-3">
                        <div className="ml-1 text-black-500 text-[16px] font-[600]">
                            {intl.formatMessage({ id: "i02017" })}
                        </div>

                        <DateRangePicker max={90}
                                         period={period}
                                         onChangePeriod={setPeriod}
                        >
                            <Button size="xs"
                                    className="w-[172px] py-0.5 flex items-center justify-between bg-white border border-black-300 text-black-500 hover:text-chat42 hover:bg-chat42-10 hover:border-chat42"
                            >
                                <div>{period.start?.clone().format("YYYY.MM.DD")}</div>
                                <div>~</div>
                                <div>{period.end?.clone().format("YYYY.MM.DD")}</div>
                            </Button>
                        </DateRangePicker>
                    </div>

                    <div className={classNames(
                             "h-[380px] flex gap-2.5",
                             !statCounts && "hidden"
                         )}
                    >
                        <div className="size-full flex flex-col gap-2.5">
                            <div className="ml-1 text-black-500 text-[15px] font-[600] leading-[1.2]">
                                {intl.formatMessage({ id: "i02009" })}
                            </div>

                            <div className="size-full py-6 px-8 flex justify-center items-center border border-black-200 rounded-[6px] bg-white">
                                <SentenceLineChart statistics={statCounts?.request}
                                                   dateRange={period}
                                />
                            </div>
                        </div>

                        <div className="flex-none w-80 flex flex-col gap-2.5">
                            <div className="ml-1 text-black-500 text-[15px] font-[600] leading-[1.2]">
                                {intl.formatMessage({ id: "i02008" })}
                            </div>
                            
                            <div className="size-full flex justify-center items-center gap-5 border border-black-200 rounded-[6px] bg-white">
                                <IntentStatTable data={statCounts?.intent} />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2.5">
                        <div className="flex justify-between items-center">
                            <div className="ml-1 text-black-500 text-[15px] font-[600]">
                                {intl.formatMessage({ id: "i02018" })}
                            </div>
                            <Button variant="text"
                                    onClick={onClickDownload}
                            >
                                <DownloadIcon className="size-4" />
                                {intl.formatMessage({ id: "b0005" })}
                            </Button>
                        </div>

                        <div className="max-h-[756px] w-full">
                            <UserSentencesTable period={period} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardView;
